(() => {
  const SELECTOR = 'zoom-dialog';
  const ACTIVE_ATTR = 'data-fm-lightbox-active';

  function findRequestedIndex(event, gallery) {
    let node = event.target instanceof Element ? event.target : null;

    while (node && node !== gallery) {
      const action = node.getAttribute?.('on:click') || '';
      const match = action.match(/\/(?:open|zoom)\/(\d+)/);

      if (match) return Number(match[1]);
      node = node.parentElement;
    }

    return null;
  }

  function pauseInactiveMedia(items, activeIndex) {
    items.forEach((item, index) => {
      if (index === activeIndex) return;
      item.querySelectorAll('video').forEach((video) => video.pause());
    });
  }

  function initZoomDialog(zoom) {
    if (zoom.dataset.fmLightboxReady === 'true') return;

    const dialog = zoom.querySelector(':scope > dialog.dialog-zoomed-gallery__dialog');
    const gallery = dialog?.querySelector('.dialog-zoomed-gallery');
    const thumbnails = dialog?.querySelector('.dialog-thumbnails-list');

    if (!dialog || !gallery || !thumbnails) return;

    const mediaItems = Array.from(gallery.children).filter((item) =>
      item.classList.contains('product-media-container')
    );
    const thumbButtons = Array.from(thumbnails.querySelectorAll('.dialog-thumbnails-list__thumbnail'));

    if (!mediaItems.length) return;

    zoom.dataset.fmLightboxReady = 'true';

    const counter = document.createElement('p');
    counter.className = 'fm-lightbox__counter';
    counter.setAttribute('aria-live', 'polite');

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'fm-lightbox__nav fm-lightbox__nav--prev';
    prev.setAttribute('aria-label', 'Imagen anterior');
    prev.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5 8 12l7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'fm-lightbox__nav fm-lightbox__nav--next';
    next.setAttribute('aria-label', 'Imagen siguiente');
    next.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    dialog.append(counter, prev, next);

    let activeIndex = 0;

    function normalizeIndex(index) {
      const length = mediaItems.length;
      return ((index % length) + length) % length;
    }

    function updateControls() {
      counter.textContent = `${activeIndex + 1} / ${mediaItems.length}`;
      const hideNavigation = mediaItems.length < 2;
      prev.hidden = hideNavigation;
      next.hidden = hideNavigation;
    }

    function setActive(index, options = {}) {
      const { updateThumbs = true, focusThumb = true } = options;
      activeIndex = normalizeIndex(index);

      mediaItems.forEach((item, itemIndex) => {
        const isActive = itemIndex === activeIndex;

        if (isActive) {
          item.setAttribute(ACTIVE_ATTR, 'true');
          item.setAttribute('aria-hidden', 'false');
        } else {
          item.removeAttribute(ACTIVE_ATTR);
          item.setAttribute('aria-hidden', 'true');
        }
      });

      if (updateThumbs) {
        thumbButtons.forEach((button, buttonIndex) => {
          button.setAttribute('aria-selected', buttonIndex === activeIndex ? 'true' : 'false');
        });
      }

      const activeThumb = thumbButtons[activeIndex];
      if (focusThumb && activeThumb) {
        activeThumb.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }

      pauseInactiveMedia(mediaItems, activeIndex);
      updateControls();

      if (typeof zoom.loadHighResolutionImage === 'function') {
        zoom.loadHighResolutionImage(mediaItems[activeIndex]);
      }
    }

    function go(delta) {
      setActive(activeIndex + delta);
    }

    prev.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      go(-1);
    });

    next.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      go(1);
    });

    thumbnails.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.dialog-thumbnails-list__thumbnail');
        if (!button) return;

        const index = thumbButtons.indexOf(button);
        if (index >= 0) setActive(index, { updateThumbs: true, focusThumb: false });
      },
      true
    );

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(1);
      }
    });

    dialog.addEventListener(
      'wheel',
      (event) => {
        if (dialog.open) event.preventDefault();
      },
      { passive: false }
    );

    const openObserver = new MutationObserver(() => {
      if (!dialog.open) {
        pauseInactiveMedia(mediaItems, -1);
        return;
      }

      const requested = Number(zoom.dataset.fmRequestedIndex);
      const selected = thumbButtons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
      const index = Number.isInteger(requested) && requested >= 0 ? requested : selected >= 0 ? selected : 0;

      setActive(index, { focusThumb: false });
      delete zoom.dataset.fmRequestedIndex;
    });

    openObserver.observe(dialog, {
      attributes: true,
      attributeFilter: ['open']
    });

    setActive(0, { focusThumb: false });
  }

  function initAll(root = document) {
    root.querySelectorAll(SELECTOR).forEach(initZoomDialog);
  }

  document.addEventListener(
    'click',
    (event) => {
      const gallery = event.target instanceof Element ? event.target.closest('media-gallery') : null;
      if (!gallery) return;

      const index = findRequestedIndex(event, gallery);
      if (index === null) return;

      const zoom = gallery.querySelector(SELECTOR);
      if (zoom) zoom.dataset.fmRequestedIndex = String(index);
    },
    true
  );

  function startObserver() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          if (node.matches?.(SELECTOR)) initZoomDialog(node);
          node.querySelectorAll?.(SELECTOR).forEach(initZoomDialog);
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function boot() {
    initAll();
    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
